<template>
    <div class="modal modal-xl" id="importModal" tabindex="-1" aria-hidden="true" data-bs-backdrop="static">
        <div class="modal-dialog">
            <div class="modal-content">
                <div class="modal-header">
                    <h4 class="modal-title">Importera grunddatafil</h4>
                    <button type="button" class="btn-close" aria-label="Close" @click="$emit('close')"></button>
                </div>
                <div class="modal-body">
                    <div>
                    </div>
                    <div class="row mb-3 mt-3">
                        <div class="col-6">
                            <input ref="fileInput"  class="filestyle form-control" type="file" id="fileInput" accept=".xlsx" :key="data.fileInputKey" />
                        </div>
                        <div class="col-2">
                            <button type="button" class="btn btn-primary ms-2" @click="onReadFile">Läs in fil</button>
                        </div>
                        <div class="col-4 text-end">
                            <a href="https://wiki.oneviewer.se/doku.php?id=oneviewer:grunddata" target="_blank" rel="noopener noreferrer">
                                Instruktioner för grunddataimport&nbsp;
                                <i class="bi bi-box-arrow-up-right"></i>
                            </a>
                        </div>
                        <div class="text-danger" v-show="noFileSelected">Vänligen välj en fil att ladda upp</div>
                    </div>
                    <div class="alert alert-danger" role="alert" v-if="props.data.errorMessage">
                        {{ props.data.errorMessage }}
                    </div>
                    <div class="text-success mb-3" v-if="props.data.successMessage">
                        {{ props.data.successMessage }}
                    </div>
                    <div class="table-responsive">
                        <table class="table table-bordered" v-if="props.data.diff && props.data.diff.length > 0">
                            <thead>
                                <tr>
                                    <th>Rad</th>
                                    <th>Åtgärd</th>
                                    <th>Nummer</th>
                                    <th>Adress</th>
                                    <th>Typ</th>
                                    <th>Externt nummer</th>
                                    <th>Hustyp</th>
                                    <th>Andel</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="diff in props.data.diff" :key="diff.row">
                                    <td>{{ diff.row }}</td>
                                    <td :class="diff.id ? (diff.delete ? 'text-danger' : (diff.hasChange ? 'text-warning' : '')) : 'text-success'" :title="diff.error">{{ diff.id ? (diff.delete ? 'Ta bort' : (diff.hasChange ? 'Ändra' : 'Ingen')) : 'Ny' }}</td>
                                    <td :class="(diff.delete ? 'text-decoration-line-through ' : '') + (diff.number.error ? 'table-danger' : (diff.number.oldValue || diff.number.oldValue === '' ? 'table-warning' : ''))" :title="diff.number.error ? diff.number.error : (diff.number.oldValue ? 'Tidigare: ' + diff.number.oldValue : '')">{{ diff.number.value }}</td>
                                    <td :class="(diff.delete ? 'text-decoration-line-through ' : '') + (diff.address?.oldValue || diff.address?.oldValue === '' ? 'table-warning' : '')" :title="diff.address?.oldValue ? 'Tidigare: ' + diff.address?.oldValue : ''">{{ diff.address?.value }}</td>
                                    <td :class="(diff.delete ? 'text-decoration-line-through ' : '') + (diff.type?.oldValue || diff.type?.oldValue === '' ? 'table-warning' : '')" :title="diff.type?.oldValue ? 'Tidigare: ' + diff.type?.oldValue : ''">{{ diff.type?.value }}</td>
                                    <td :class="(diff.delete ? 'text-decoration-line-through ' : '') + (diff.externalNumber?.oldValue || diff.externalNumber?.oldValue === '' ? 'table-warning' : '')" :title="diff.externalNumber?.oldValue ? 'Tidigare: ' + diff.externalNumber?.oldValue : ''">{{ diff.externalNumber?.value }}</td>
                                    <td :class="(diff.delete ? 'text-decoration-line-through ' : '') + (diff.buildingType?.oldValue || diff.buildingType?.oldValue === '' ? 'table-warning' : '')" :title="diff.buildingType?.oldValue ? 'Tidigare: ' + diff.buildingType?.oldValue : ''">{{ diff.buildingType?.value }}</td>
                                    <td :class="(diff.delete ? 'text-decoration-line-through ' : '') + (diff.share?.oldValue || diff.share?.oldValue === '' ? 'table-warning' : '')" :title="diff.share?.oldValue ? 'Tidigare: ' + diff.share?.oldValue : ''">{{ diff.share?.value.toLocaleString('sv-SE') }}</td>
                                </tr>
                            </tbody>
                        </table>

                    </div>
                </div>
                <div class="modal-footer">
                    <button type="button" class="btn btn-secondary" @click="$emit('close')">Avbryt</button>
                    <button type="button" class="btn btn-success" @click="onPerformChanges" :disabled="!props.data.readyToApply">Genomför ändringar</button>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref } from 'vue'
import readXlsxFile from 'read-excel-file'
import sitesService from '@/services/sitesService'

const props = defineProps({
    data: Object,
})

const emit = defineEmits(['close', 'reload'])

const fileInput = ref(null)
const noFileSelected = ref(false)

async function onReadFile() {
    props.data.errorMessage = null
    props.data.errorLines = []
    props.data.successMessage = null

    const file = fileInput.value.files[0]

    if (!file) {
        props.data.errorMessage = 'Ingen fil vald'
        props.data.fileInputKey++
        return
    }

    const reader = new FileReader()
    let content = null

    reader.onload = res => {
        content = res.target.result
    }
    reader.onerror = () => {
        props.data.errorMessage = 'Fel vid inläsning av filen'
        props.data.fileInputKey++
    }
    reader.onloadend = async () => {
        try {
            const rows = await readXlsxFile(content)

            if (rows.length === 0) {
                props.data.errorMessage = 'Filen är tom'
                props.data.fileInputKey++
                return
            }

            const headers = {}
            let headersCount = 0
            for (let i = 0; i < rows[0].length; i++) {
                if (!rows[0][i]) {
                    continue
                }
                const header = String(rows[0][i])
                if (headers[header] !== undefined) {
                    props.data.errorMessage = `Kolumnen "${header}" förekommer mer än en gång`
                    props.data.fileInputKey++
                    return
                }
                headers[header] = i
                headersCount++
            }

            if (headers['Mätplatsnummer'] == null) {
                props.data.errorMessage = 'Kolumnen "Mätplatsnummer" saknas'
                props.data.fileInputKey++
                return
            }

            const apartments = []

            let rowNumber = 1
            for (let row of rows) {
                if (rowNumber === 1) {
                    // Skip header row
                    rowNumber++
                    continue
                }
                apartments.push({
                    row: rowNumber++,
                    number: row[headers['Mätplatsnummer']],
                    address: headers['Adress'] != null ? row[headers['Adress']] : null,
                    type: headers['Typ'] != null ? row[headers['Typ']] : null,
                    externalNumber: headers['Externt nummer'] != null ? row[headers['Externt nummer']] : null,
                    buildingType: headers['Hustyp'] != null ? row[headers['Hustyp']] : null,
                    share: headers['Andel'] != null ? row[headers['Andel']] : null,
                    newNumber: headers['Nytt mätplatsnummer'] != null ? row[headers['Nytt mätplatsnummer']] : null,
                    delete: headers['Ta bort?'] != null ? row[headers['Ta bort?']] : null,
                })
            }

            if (apartments.length === 0) {
                props.data.errorMessage = 'Filen är tom förutom rubrikraden'
                props.data.fileInputKey++
                return
            }

            const diffResult = (await sitesService.diffBaseData(props.data.siteId, apartments)).data
            props.data.changes = apartments

            props.data.diff = diffResult.result
            props.data.readyToApply = diffResult.result.length > 0 && !diffResult.thereAreErrors && diffResult.thereAreChanges

            if (diffResult.thereAreErrors) {
                props.data.errorMessage = 'Det finns fel i filen, se röda markeringar nedan.'
            } else if (!diffResult.thereAreChanges) {
                props.data.errorMessage = 'Inga ändringar att genomföra.'
            } else {
                props.data.successMessage = 'Vänligen granska ändringarna (gula fält nedan) och klicka sedan på "Genomför ändringar" om allt stämmer.'
            }
        } catch (error) {
            props.data.fileInputKey++
        }
    }
    reader.readAsArrayBuffer(file)
}

async function onPerformChanges() {
    try {
        if (!props.data.readyToApply || !props.data.changes || props.data.changes.length === 0) {
            return
        }

        await sitesService.importBaseData(props.data.siteId, props.data.changes)

        emit('reload')
        emit('close')
    } catch (error) {
        console.error(error)
        props.data.errorMessage = 'Fel vid genomförande av ändringar'
    }
}

</script>